/**
 * Item J — RSA editor (operator review/edit of AI-generated ad copy).
 *
 * Renders the responsive-search-ad drafts a campaign run parked at AD_REVIEW,
 * keyed by cluster name (2 RSAs per cluster). The operator edits headlines
 * (3–15, ≤30 chars), descriptions (2–4, ≤90 chars), pins, and display paths
 * (≤15 chars), then submits the edited copy back to the run.
 *
 * Validation is computed live per-RSA and rolled up to an overall `isValid`
 * that gates the Submit button. Empty headline/description rows are stripped on
 * submit; pins and paths are preserved.
 */
import { useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CampaignBuildingLoader } from '@/components/google-ads/CampaignBuildingLoader';
import type {
  RsaDraft,
} from '@/services/googleAdsCampaignsService';

const HEADLINE_MAX = 30;
const HEADLINE_MIN = 3;
const HEADLINE_CAP = 15;
const DESCRIPTION_MAX = 90;
const DESCRIPTION_MIN = 2;
const DESCRIPTION_CAP = 4;
const PATH_MAX = 15;
// Google allows 1–3 responsive search ads per ad group.
const RSA_MAX = 3;
const RSA_MIN = 1;

/** A blank RSA with the minimum-count row structure visible (invalid until filled). */
function blankRsa(): RsaDraft {
  return {
    headlines: [
      { text: '', pin: null },
      { text: '', pin: null },
      { text: '', pin: null },
    ],
    descriptions: [
      { text: '', pin: null },
      { text: '', pin: null },
    ],
    path1: '',
    path2: '',
    name: '',
    final_url: '',
    from_fallback: false,
  };
}

/** Regex an operator-entered Final URL must satisfy when non-empty. */
const FINAL_URL_RE = /^https?:\/\/.+/i;
const AD_NAME_MAX = 255;

type HeadlinePin = 1 | 2 | 3 | null;
type DescriptionPin = 1 | 2 | null;

// ---------------------------------------------------------------------------
// Reusable char-count input — an ui/input with a muted "N/max" overlay that
// flips to text-destructive once the value exceeds `max`.
// ---------------------------------------------------------------------------
function CharCountInput({
  value,
  max,
  onChange,
  placeholder,
  ariaLabel,
  invalid = false,
}: {
  value: string;
  max: number;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** Flags a non-length problem (e.g. all-caps copy) so the row highlights. */
  invalid?: boolean;
}) {
  const over = value.length > max;
  const bad = over || invalid;
  return (
    <div className="relative flex-1">
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn(
          'pr-14',
          bad && 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20',
        )}
      />
      <span
        className={cn(
          'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] tabular-nums',
          over ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {value.length}/{max}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compact pin control — "Unpinned" + one item per allowed served position.
// ---------------------------------------------------------------------------
function PinSelect({
  value,
  positions,
  onChange,
  ariaLabel,
}: {
  value: number | null;
  positions: number[];
  onChange: (value: number | null) => void;
  ariaLabel?: string;
}) {
  return (
    <Select
      value={value == null ? 'none' : String(value)}
      onValueChange={(next) => onChange(next === 'none' ? null : Number(next))}
    >
      <SelectTrigger className="h-9 w-[108px] shrink-0 text-xs" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Unpinned</SelectItem>
        {positions.map((position) => (
          <SelectItem key={position} value={String(position)}>
            Pos {position}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Ad Strength — a CLIENT-SIDE ESTIMATE mirroring Google's public Ad Strength
// factors (headline volume + uniqueness, description volume, pinning). Google
// computes the authoritative strength only AFTER upload; this is guidance so the
// operator can improve copy before submitting.
//
// Scoring (0–100), tunable thresholds flagged inline:
//   Headlines   → up to 50 pts, on DISTINCT non-empty count (target 15).
//   Descriptions→ up to 35 pts, on DISTINCT non-empty count (target 4).
//   Uniqueness  → up to 15 pts, distinct/total ratio (duplicates cost points).
//   Pinning     → −4 pts per pinned asset beyond the first 2 (cap −16), since
//                 heavy pinning suppresses rotation and Google-reported strength.
// Buckets: <40 Poor · 40–64 Average · 65–84 Good · ≥85 Excellent.
// ---------------------------------------------------------------------------
type AdStrengthLabel = 'Poor' | 'Average' | 'Good' | 'Excellent';

interface AdStrength {
  score: number;
  label: AdStrengthLabel;
  tips: string[];
}

function computeAdStrength(rsa: RsaDraft): AdStrength {
  const headTexts = rsa.headlines.map((h) => h.text.trim()).filter(Boolean);
  const descTexts = rsa.descriptions.map((d) => d.text.trim()).filter(Boolean);
  const distinctHeads = new Set(headTexts.map((t) => t.toLowerCase())).size;
  const distinctDescs = new Set(descTexts.map((t) => t.toLowerCase())).size;

  // Headlines → up to 50 pts (target 15 distinct; "excellent" band starts at 13).
  let headPts: number;
  if (distinctHeads >= 13) headPts = 50;
  else if (distinctHeads >= 10) headPts = 44;
  else if (distinctHeads >= 8) headPts = 38;
  else if (distinctHeads >= 5) headPts = 26;
  else if (distinctHeads >= 3) headPts = 15;
  else headPts = distinctHeads * 4;

  // Descriptions → up to 35 pts (target 4 distinct).
  let descPts: number;
  if (distinctDescs >= 4) descPts = 35;
  else if (distinctDescs >= 3) descPts = 26;
  else if (distinctDescs >= 2) descPts = 16;
  else descPts = distinctDescs * 6;

  // Uniqueness → up to 15 pts; duplicate assets drag the distinct/total ratio down.
  const totalNonEmpty = headTexts.length + descTexts.length;
  const totalDistinct = distinctHeads + distinctDescs;
  const uniquenessPts =
    totalNonEmpty === 0 ? 0 : Math.round((totalDistinct / totalNonEmpty) * 15);

  // Pinning penalty — first 2 pins are free, then −4 each (cap −16).
  const pinnedCount =
    rsa.headlines.filter((h) => h.pin != null && h.text.trim()).length +
    rsa.descriptions.filter((d) => d.pin != null && d.text.trim()).length;
  const pinPenalty = pinnedCount <= 2 ? 0 : Math.min((pinnedCount - 2) * 4, 16);

  const score = Math.max(
    0,
    Math.min(100, headPts + descPts + uniquenessPts - pinPenalty),
  );

  let label: AdStrengthLabel;
  if (score >= 85) label = 'Excellent';
  else if (score >= 65) label = 'Good';
  else if (score >= 40) label = 'Average';
  else label = 'Poor';

  // Improvement tips from the weakest factors (mirrors the CSV "Ad strength
  // improvements"); show at most 3, weakest-first.
  const tips: string[] = [];
  if (distinctHeads < 8) tips.push('Add more headlines');
  if (headTexts.length > distinctHeads) tips.push('Make headlines more unique');
  if (distinctDescs < 4) tips.push('Add more descriptions');
  if (descTexts.length > distinctDescs) tips.push('Make descriptions more unique');
  if (pinPenalty > 0) tips.push('Unpin some assets to improve rotation');

  return { score, label, tips: tips.slice(0, 3) };
}

/** Tailwind theme-token classes per strength bucket (bar fill, text, chip border). */
const STRENGTH_STYLES: Record<
  AdStrengthLabel,
  { bar: string; text: string; chipBorder: string }
> = {
  Poor: { bar: 'bg-destructive', text: 'text-destructive', chipBorder: 'border-destructive/40' },
  Average: { bar: 'bg-amber', text: 'text-amber-dark', chipBorder: 'border-amber/40' },
  Good: { bar: 'bg-[hsl(var(--status-success))]', text: 'text-[hsl(var(--status-success))]', chipBorder: 'border-[hsl(var(--status-success))]/40' },
  Excellent: { bar: 'bg-[hsl(var(--status-success))]', text: 'text-[hsl(var(--status-success))]', chipBorder: 'border-[hsl(var(--status-success))]/40' },
};

/** Labeled Ad Strength meter — bar + label + up to 3 improvement tips. */
function AdStrengthMeter({ strength }: { strength: AdStrength }) {
  const style = STRENGTH_STYLES[strength.label];
  return (
    <div className="rounded-md border border-border bg-warm-bg p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">Ad strength (estimate)</span>
        <span className={cn('text-xs font-semibold', style.text)}>{strength.label}</span>
      </div>
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={strength.score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Estimated ad strength: ${strength.label}`}
      >
        <div
          className={cn('h-full rounded-full transition-all', style.bar)}
          style={{ width: `${Math.max(strength.score, 4)}%` }}
        />
      </div>
      {strength.tips.length > 0 ? (
        <ul className="mt-2 space-y-0.5">
          {strength.tips.map((tip) => (
            <li key={tip} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/60" />
              {tip}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Estimated only — Google computes the final Ad Strength after upload.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
interface RsaValidation {
  headlineCount: number;
  descriptionCount: number;
  headlineOverLimit: boolean;
  descriptionOverLimit: boolean;
  pathOverLimit: boolean;
  finalUrlInvalid: boolean;
  /** Per-row all-caps flags, so the offending input can be highlighted. */
  headlineAllCaps: boolean[];
  descriptionAllCaps: boolean[];
  hasAllCaps: boolean;
  valid: boolean;
}

function nonEmpty(text: string): boolean {
  return text.trim().length > 0;
}

/**
 * Google rejects all-caps ad copy, and the backend enforces it at ASSET_CREATE
 * (`rsa_validator._is_all_caps_text`) — a run FAILS at publish otherwise. Mirror
 * that rule EXACTLY here so it's caught in the editor before submit:
 * ≥6 letters and not a single lowercase letter among them.
 */
function isAllCapsText(text: string): boolean {
  const letters = (text ?? '').match(/[A-Za-z]/g) ?? [];
  if (letters.length < 6) return false;
  return !letters.some((letter) => letter === letter.toLowerCase());
}

function validateRsa(rsa: RsaDraft): RsaValidation {
  const validHeadlines = rsa.headlines.filter(
    (h) => nonEmpty(h.text) && h.text.length <= HEADLINE_MAX,
  ).length;
  const validDescriptions = rsa.descriptions.filter(
    (d) => nonEmpty(d.text) && d.text.length <= DESCRIPTION_MAX,
  ).length;
  const headlineOverLimit = rsa.headlines.some((h) => h.text.length > HEADLINE_MAX);
  const descriptionOverLimit = rsa.descriptions.some(
    (d) => d.text.length > DESCRIPTION_MAX,
  );
  const pathOverLimit = rsa.path1.length > PATH_MAX || rsa.path2.length > PATH_MAX;
  // Empty final_url is allowed (backend defaults it to the landing page); a
  // non-empty value must look like an http(s) URL.
  const finalUrlInvalid = nonEmpty(rsa.final_url) && !FINAL_URL_RE.test(rsa.final_url.trim());
  // All-caps copy is rejected by Google (backend fails the run at publish).
  const headlineAllCaps = rsa.headlines.map((h) => isAllCapsText(h.text));
  const descriptionAllCaps = rsa.descriptions.map((d) => isAllCapsText(d.text));
  const hasAllCaps =
    headlineAllCaps.some(Boolean) || descriptionAllCaps.some(Boolean);

  const valid =
    validHeadlines >= HEADLINE_MIN &&
    validDescriptions >= DESCRIPTION_MIN &&
    !headlineOverLimit &&
    !descriptionOverLimit &&
    !pathOverLimit &&
    !finalUrlInvalid &&
    !hasAllCaps;

  return {
    headlineCount: validHeadlines,
    descriptionCount: validDescriptions,
    headlineOverLimit,
    descriptionOverLimit,
    pathOverLimit,
    finalUrlInvalid,
    headlineAllCaps,
    descriptionAllCaps,
    hasAllCaps,
    valid,
  };
}

/** Strip empty rows, trim text, preserve pins + paths + fallback flag. */
function cleanRsa(rsa: RsaDraft): RsaDraft {
  return {
    headlines: rsa.headlines
      .filter((h) => nonEmpty(h.text))
      .map((h) => ({ text: h.text.trim(), pin: h.pin })),
    descriptions: rsa.descriptions
      .filter((d) => nonEmpty(d.text))
      .map((d) => ({ text: d.text.trim(), pin: d.pin })),
    path1: rsa.path1.trim(),
    path2: rsa.path2.trim(),
    name: rsa.name.trim(),
    final_url: rsa.final_url.trim(),
    from_fallback: rsa.from_fallback,
  };
}

/** Normalize a single RSA draft to editor-safe defaults (never-null text/pin). */
function normalizeRsa(rsa: RsaDraft): RsaDraft {
  return {
    headlines: rsa.headlines.map((h) => ({ text: h.text ?? '', pin: h.pin ?? null })),
    descriptions: rsa.descriptions.map((d) => ({ text: d.text ?? '', pin: d.pin ?? null })),
    path1: rsa.path1 ?? '',
    path2: rsa.path2 ?? '',
    name: rsa.name ?? '',
    final_url: rsa.final_url ?? '',
    from_fallback: rsa.from_fallback,
  };
}

function deepCopyDrafts(
  drafts: Record<string, RsaDraft[]>,
): Record<string, RsaDraft[]> {
  return Object.fromEntries(
    Object.entries(drafts).map(([cluster, rsas]) => [
      cluster,
      rsas.map(normalizeRsa),
    ]),
  );
}

/**
 * Stable signature of an RSA's *text* only (headlines + descriptions), used to
 * detect whether the operator has edited a fallback draft away from its
 * generated placeholder copy. Pins and paths are intentionally excluded — only
 * headline/description text edits count as "handling" the placeholder.
 */
function textSignature(rsa: RsaDraft): string {
  const heads = rsa.headlines.map((h) => h.text).join('␞');
  const descs = rsa.descriptions.map((d) => d.text).join('␞');
  return `${heads}␟${descs}`;
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------
export function RsaEditor({
  drafts,
  onSubmit,
  onReject,
  submitting,
  onRegenerate,
}: {
  drafts: Record<string, RsaDraft[]>;
  onSubmit: (adCopy: Record<string, RsaDraft[]>) => void;
  onReject: () => void;
  submitting: boolean;
  /**
   * Force a fresh AI regeneration of a single RSA. Returns the new draft, which
   * the editor applies to that card's local state (leaving every other card's
   * in-progress edits untouched). A non-fallback result clears its
   * fallback-block. Omit to hide the per-ad "Regenerate with AI" button.
   */
  onRegenerate?: (clusterName: string, variantIndex: number) => Promise<RsaDraft>;
}) {
  const [draftState, setDraftState] = useState<Record<string, RsaDraft[]>>(() =>
    deepCopyDrafts(drafts),
  );
  // Snapshot of the drafts as first mounted — the baseline we compare against to
  // decide whether a `from_fallback` ad is still an unhandled placeholder. A
  // regenerate re-baselines only the regenerated card (see handleRegenerate).
  const [snapshot, setSnapshot] = useState<Record<string, RsaDraft[]>>(() =>
    deepCopyDrafts(drafts),
  );
  // `${cluster}:${idx}` of the ad currently regenerating, or null.
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);

  // Per-ad expanded/collapsed state, keyed `${cluster}:${idx}`. Computed ONCE on
  // mount: an ad opens if it needs attention (invalid OR an unresolved
  // placeholder), otherwise it starts collapsed. After mount the user toggles
  // freely; add/remove re-key this map (see addAd/removeAd) and regenerate/add
  // auto-open an ad that lands needing attention.
  const [openAds, setOpenAds] = useState<Record<string, boolean>>(() => {
    const initial = deepCopyDrafts(drafts);
    const map: Record<string, boolean> = {};
    for (const [cluster, rsas] of Object.entries(initial)) {
      rsas.forEach((rsa, idx) => {
        // At mount draftState === snapshot, so an unresolved fallback is simply
        // `from_fallback`. Combine with format validity.
        map[`${cluster}:${idx}`] =
          !validateRsa(rsa).valid || Boolean(rsa.from_fallback);
      });
    }
    return map;
  });

  const setAdOpen = (key: string, open: boolean) =>
    setOpenAds((prev) => ({ ...prev, [key]: open }));

  const validations = useMemo(() => {
    const map: Record<string, RsaValidation[]> = {};
    for (const [cluster, rsas] of Object.entries(draftState)) {
      map[cluster] = rsas.map(validateRsa);
    }
    return map;
  }, [draftState]);

  // Live per-RSA Ad Strength estimate (recomputed with the draft state).
  const strengths = useMemo(() => {
    const map: Record<string, AdStrength[]> = {};
    for (const [cluster, rsas] of Object.entries(draftState)) {
      map[cluster] = rsas.map(computeAdStrength);
    }
    return map;
  }, [draftState]);

  // An ad is an *unresolved fallback* while its baseline was `from_fallback` AND
  // it is still `from_fallback` AND its text is unchanged from that baseline.
  // Editing any headline/description text OR regenerating to a non-fallback
  // draft clears it. Such ads block submit even though they may be format-valid.
  const isUnresolvedFallback = useMemo(() => {
    const map: Record<string, boolean[]> = {};
    for (const [cluster, rsas] of Object.entries(draftState)) {
      map[cluster] = rsas.map((rsa, idx) => {
        const base = snapshot[cluster]?.[idx];
        if (!base?.from_fallback) return false;
        return Boolean(rsa.from_fallback) && textSignature(rsa) === textSignature(base);
      });
    }
    return map;
  }, [draftState, snapshot]);

  const hasUnresolvedFallback = useMemo(
    () => Object.values(isUnresolvedFallback).some((list) => list.some(Boolean)),
    [isUnresolvedFallback],
  );

  const isValid = useMemo(
    () =>
      Object.keys(draftState).length > 0 &&
      Object.values(validations).every((list) => list.every((v) => v.valid)) &&
      !hasUnresolvedFallback,
    [validations, draftState, hasUnresolvedFallback],
  );

  const handleRegenerate = async (cluster: string, idx: number) => {
    if (!onRegenerate || regeneratingKey) return;
    const key = `${cluster}:${idx}`;
    setRegeneratingKey(key);
    try {
      const fresh = normalizeRsa(await onRegenerate(cluster, idx));
      // Replace only this card's state + re-baseline only this card, so other
      // cards keep their in-progress edits. If `fresh.from_fallback` is false
      // the fallback-block clears; if it's still a fallback the card stays
      // unresolved until the operator edits or regenerates again.
      setDraftState((prev) => {
        const list = prev[cluster]?.slice();
        if (!list) return prev;
        list[idx] = fresh;
        return { ...prev, [cluster]: list };
      });
      setSnapshot((prev) => {
        const list = prev[cluster]?.slice();
        if (!list) return prev;
        list[idx] = fresh;
        return { ...prev, [cluster]: list };
      });
      // Auto-open the card if the regenerated copy still needs attention
      // (invalid or another placeholder); leave it as the user left it otherwise.
      if (!validateRsa(fresh).valid || Boolean(fresh.from_fallback)) {
        setAdOpen(key, true);
      }
    } catch {
      // The parent mutation surfaces the error toast; just reset local state.
    } finally {
      setRegeneratingKey(null);
    }
  };

  // ---- add / remove whole ads (RSAs) per cluster ------------------------
  // Append a blank RSA (cap RSA_MAX). Kept in lock-step across draftState +
  // snapshot so index-keyed validation/fallback checks stay aligned. The new
  // card starts EXPANDED (it's empty → needs attention).
  const addAd = (cluster: string) => {
    const current = draftState[cluster] ?? [];
    if (current.length >= RSA_MAX) return;
    const newIdx = current.length;
    // Seed a manually-added ad from the cluster's existing ads so it starts
    // submittable, not blank-and-invalid: same Final URL and display paths (an
    // empty Final URL fails validation and blocks submit), plus the "{Cluster}
    // · Ad N" label AI drafts get. Headlines/descriptions stay empty — that's
    // the copy the operator is here to write.
    const sibling = current[0];
    const fresh = normalizeRsa({
      ...blankRsa(),
      name: `${cluster} · Ad ${newIdx + 1}`.slice(0, AD_NAME_MAX),
      final_url: sibling?.final_url ?? '',
      path1: sibling?.path1 ?? '',
      path2: sibling?.path2 ?? '',
    });
    setDraftState((prev) => {
      const list = prev[cluster] ?? [];
      if (list.length >= RSA_MAX) return prev;
      return { ...prev, [cluster]: [...list, fresh] };
    });
    setSnapshot((prev) => {
      const list = prev[cluster] ?? [];
      if (list.length >= RSA_MAX) return prev;
      return { ...prev, [cluster]: [...list, fresh] };
    });
    setAdOpen(`${cluster}:${newIdx}`, true);
  };

  // Remove one RSA (floor RSA_MIN). Drops it from draftState + snapshot and
  // re-indexes the per-ad open-state map for this cluster so keys `${cluster}:i`
  // for i > idx shift down by one (they were index-keyed).
  const removeAd = (cluster: string, idx: number) => {
    const current = draftState[cluster] ?? [];
    if (current.length <= RSA_MIN) return;
    setDraftState((prev) => {
      const list = prev[cluster];
      if (!list || list.length <= RSA_MIN) return prev;
      return { ...prev, [cluster]: list.filter((_, i) => i !== idx) };
    });
    setSnapshot((prev) => {
      const list = prev[cluster];
      if (!list || list.length <= RSA_MIN) return prev;
      return { ...prev, [cluster]: list.filter((_, i) => i !== idx) };
    });
    setOpenAds((prev) => {
      const next: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(prev)) {
        const sep = k.lastIndexOf(':');
        const kc = k.slice(0, sep);
        const ki = Number(k.slice(sep + 1));
        if (kc !== cluster) {
          next[k] = v;
          continue;
        }
        if (ki === idx) continue; // dropped
        next[`${cluster}:${ki > idx ? ki - 1 : ki}`] = v;
      }
      return next;
    });
  };

  // ---- immutable update helpers -----------------------------------------
  const mutateRsa = (
    cluster: string,
    idx: number,
    updater: (rsa: RsaDraft) => RsaDraft,
  ) => {
    setDraftState((prev) => {
      const list = prev[cluster]?.slice();
      if (!list) return prev;
      list[idx] = updater(list[idx]);
      return { ...prev, [cluster]: list };
    });
  };

  const setHeadlineText = (cluster: string, idx: number, hIdx: number, text: string) =>
    mutateRsa(cluster, idx, (rsa) => {
      const headlines = rsa.headlines.slice();
      headlines[hIdx] = { ...headlines[hIdx], text };
      return { ...rsa, headlines };
    });

  const setHeadlinePin = (cluster: string, idx: number, hIdx: number, pin: HeadlinePin) =>
    mutateRsa(cluster, idx, (rsa) => {
      const headlines = rsa.headlines.slice();
      headlines[hIdx] = { ...headlines[hIdx], pin };
      return { ...rsa, headlines };
    });

  const addHeadline = (cluster: string, idx: number) =>
    mutateRsa(cluster, idx, (rsa) =>
      rsa.headlines.length >= HEADLINE_CAP
        ? rsa
        : { ...rsa, headlines: [...rsa.headlines, { text: '', pin: null }] },
    );

  const removeHeadline = (cluster: string, idx: number, hIdx: number) =>
    mutateRsa(cluster, idx, (rsa) => ({
      ...rsa,
      headlines: rsa.headlines.filter((_, i) => i !== hIdx),
    }));

  const moveHeadline = (cluster: string, idx: number, hIdx: number, dir: -1 | 1) =>
    mutateRsa(cluster, idx, (rsa) => {
      const target = hIdx + dir;
      if (target < 0 || target >= rsa.headlines.length) return rsa;
      const headlines = rsa.headlines.slice();
      [headlines[hIdx], headlines[target]] = [headlines[target], headlines[hIdx]];
      return { ...rsa, headlines };
    });

  const setDescriptionText = (cluster: string, idx: number, dIdx: number, text: string) =>
    mutateRsa(cluster, idx, (rsa) => {
      const descriptions = rsa.descriptions.slice();
      descriptions[dIdx] = { ...descriptions[dIdx], text };
      return { ...rsa, descriptions };
    });

  const setDescriptionPin = (
    cluster: string,
    idx: number,
    dIdx: number,
    pin: DescriptionPin,
  ) =>
    mutateRsa(cluster, idx, (rsa) => {
      const descriptions = rsa.descriptions.slice();
      descriptions[dIdx] = { ...descriptions[dIdx], pin };
      return { ...rsa, descriptions };
    });

  const addDescription = (cluster: string, idx: number) =>
    mutateRsa(cluster, idx, (rsa) =>
      rsa.descriptions.length >= DESCRIPTION_CAP
        ? rsa
        : { ...rsa, descriptions: [...rsa.descriptions, { text: '', pin: null }] },
    );

  const removeDescription = (cluster: string, idx: number, dIdx: number) =>
    mutateRsa(cluster, idx, (rsa) => ({
      ...rsa,
      descriptions: rsa.descriptions.filter((_, i) => i !== dIdx),
    }));

  const moveDescription = (cluster: string, idx: number, dIdx: number, dir: -1 | 1) =>
    mutateRsa(cluster, idx, (rsa) => {
      const target = dIdx + dir;
      if (target < 0 || target >= rsa.descriptions.length) return rsa;
      const descriptions = rsa.descriptions.slice();
      [descriptions[dIdx], descriptions[target]] = [
        descriptions[target],
        descriptions[dIdx],
      ];
      return { ...rsa, descriptions };
    });

  const setPath = (cluster: string, idx: number, which: 'path1' | 'path2', value: string) =>
    mutateRsa(cluster, idx, (rsa) => ({ ...rsa, [which]: value }));

  const setName = (cluster: string, idx: number, value: string) =>
    mutateRsa(cluster, idx, (rsa) => ({ ...rsa, name: value }));

  const setFinalUrl = (cluster: string, idx: number, value: string) =>
    mutateRsa(cluster, idx, (rsa) => ({ ...rsa, final_url: value }));

  const buildEditedAdCopy = (): Record<string, RsaDraft[]> =>
    Object.fromEntries(
      Object.entries(draftState).map(([cluster, rsas]) => [cluster, rsas.map(cleanRsa)]),
    );

  // ---- render -----------------------------------------------------------
  const renderRsaCard = (
    cluster: string,
    idx: number,
    rsa: RsaDraft,
    validation: RsaValidation,
    unresolved: boolean,
    strength: AdStrength,
  ) => {
    const key = `${cluster}:${idx}`;
    const regenerating = regeneratingKey === key;
    const open = openAds[key] ?? true;
    const needsAttention = !validation.valid || unresolved;
    const clusterLen = draftState[cluster]?.length ?? 1;
    const strengthStyle = STRENGTH_STYLES[strength.label];
    // The ad title is the editable name. When blank, the header input shows the
    // pinned/first headline as a placeholder (falling back to "Untitled ad"), so
    // an unnamed ad still reads meaningfully without forcing a name.
    const headlinePlaceholder =
      (
        rsa.headlines.find((h) => h.pin === 1 && h.text.trim()) ??
        rsa.headlines.find((h) => h.text.trim())
      )?.text.trim() || 'Untitled ad';
    return (
    <Card key={idx} className="border-border">
      <Collapsible open={open} onOpenChange={(next) => setAdOpen(key, next)}>
        <div className="flex items-center gap-2 px-4 py-3">
          <CollapsibleTrigger className="flex shrink-0 items-center gap-2 text-left">
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                open && 'rotate-90',
              )}
            />
            <CardTitle className="shrink-0 text-sm">Ad {idx + 1}</CardTitle>
          </CollapsibleTrigger>
          {/* Editable ad title (Google Ads "Ad name"). Borderless until
              hovered/focused so it reads as a title but edits inline. */}
          <Input
            value={rsa.name}
            maxLength={AD_NAME_MAX}
            placeholder={headlinePlaceholder}
            aria-label={`Ad ${idx + 1} title`}
            onChange={(event) => setName(cluster, idx, event.target.value)}
            className="h-8 min-w-0 flex-1 border-transparent bg-transparent px-2 text-sm font-medium text-foreground shadow-none hover:border-border focus-visible:border-border"
          />
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {!open ? (
              <>
                <span className="text-xs text-muted-foreground">
                  {validation.headlineCount} headline
                  {validation.headlineCount === 1 ? '' : 's'} ·{' '}
                  {validation.descriptionCount} description
                  {validation.descriptionCount === 1 ? '' : 's'}
                </span>
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[11px] font-normal',
                    strengthStyle.chipBorder,
                    strengthStyle.text,
                  )}
                >
                  {strength.label}
                </Badge>
              </>
            ) : null}
            {needsAttention ? (
              <Badge
                variant="outline"
                className="border-destructive/40 text-[11px] font-normal text-destructive"
              >
                Needs attention
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="border-teal/40 text-[11px] font-normal text-teal-dark"
              >
                Ready
              </Badge>
            )}
            {rsa.from_fallback ? (
              <Badge
                variant="warning"
                className="gap-1 text-[11px] font-normal"
              >
                <TriangleAlert className="h-3 w-3 flex-shrink-0" />
                Placeholder
              </Badge>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
            disabled={clusterLen <= RSA_MIN || submitting || Boolean(regeneratingKey)}
            onClick={() => removeAd(cluster, idx)}
            aria-label={`Remove ad ${idx + 1}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      <CollapsibleContent>
      <CardContent className="space-y-4">
        {/* Final URL — landing page for this ad */}
        <div className="space-y-1.5">
          <label
            className="text-xs font-medium text-muted-foreground"
            htmlFor={`${key}-final-url`}
          >
            Final URL
          </label>
          <Input
            id={`${key}-final-url`}
            type="url"
            value={rsa.final_url}
            placeholder="https://example.com/landing-page"
            aria-label={`Ad ${idx + 1} final URL`}
            className={cn(
              validation.finalUrlInvalid &&
                'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20',
            )}
            onChange={(event) => setFinalUrl(cluster, idx, event.target.value)}
          />
          {validation.finalUrlInvalid ? (
            <p className="text-[11px] text-destructive">
              Enter a valid URL starting with http:// or https://
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Defaults to the campaign landing page if left blank.
            </p>
          )}
        </div>

        {/* Ad Strength — live client-side estimate */}
        <AdStrengthMeter strength={strength} />

        <div className="flex flex-wrap items-center justify-between gap-2">
          {rsa.from_fallback ? (
            <Badge
              variant="warning"
              className="flex-1 justify-start gap-1.5 rounded-md text-[11px] font-normal"
            >
              <TriangleAlert className="h-3 w-3 flex-shrink-0" />
              AI copy unavailable — generic placeholder. Edit or regenerate before
              submitting.
            </Badge>
          ) : (
            <span />
          )}
          {onRegenerate ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs text-teal-dark hover:text-teal-dark"
              disabled={regenerating || Boolean(regeneratingKey) || submitting}
              onClick={() => handleRegenerate(cluster, idx)}
              aria-label={`Regenerate ad ${idx + 1} with AI`}
            >
              {regenerating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {regenerating ? 'Regenerating…' : 'Regenerate with AI'}
            </Button>
          ) : null}
        </div>
        {unresolved ? (
          <p className="text-[11px] text-amber-dark">
            Placeholder copy — edit or regenerate to enable submit.
          </p>
        ) : null}
        {/* Headlines */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              Headlines ({validation.headlineCount}/{HEADLINE_CAP})
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-teal-dark hover:text-teal-dark"
              disabled={rsa.headlines.length >= HEADLINE_CAP}
              onClick={() => addHeadline(cluster, idx)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add headline
            </Button>
          </div>
          {rsa.headlines.map((headline, hIdx) => (
            <div key={hIdx} className="flex items-center gap-2">
              <CharCountInput
                value={headline.text}
                max={HEADLINE_MAX}
                ariaLabel={`Headline ${hIdx + 1}`}
                placeholder="Headline text"
                invalid={validation.headlineAllCaps[hIdx]}
                onChange={(text) => setHeadlineText(cluster, idx, hIdx, text)}
              />
              <PinSelect
                value={headline.pin}
                positions={[1, 2, 3]}
                ariaLabel={`Headline ${hIdx + 1} pin`}
                onChange={(pin) => setHeadlinePin(cluster, idx, hIdx, pin as HeadlinePin)}
              />
              <div className="flex shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={hIdx === 0}
                  onClick={() => moveHeadline(cluster, idx, hIdx, -1)}
                  aria-label="Move headline up"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={hIdx === rsa.headlines.length - 1}
                  onClick={() => moveHeadline(cluster, idx, hIdx, 1)}
                  aria-label="Move headline down"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={rsa.headlines.length <= 1}
                  onClick={() => removeHeadline(cluster, idx, hIdx)}
                  aria-label="Remove headline"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {validation.headlineCount < HEADLINE_MIN ? (
            <p className="text-[11px] text-destructive">
              Need at least {HEADLINE_MIN} headlines (≤{HEADLINE_MAX} chars each).
            </p>
          ) : null}
          {validation.headlineOverLimit ? (
            <p className="text-[11px] text-destructive">
              One or more headlines exceed {HEADLINE_MAX} characters.
            </p>
          ) : null}
          {validation.headlineAllCaps.some(Boolean) ? (
            <p className="text-[11px] text-destructive">
              Headlines must not be ALL CAPS — Google rejects them. Use title
              case instead.
            </p>
          ) : null}
        </div>

        {/* Descriptions */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              Descriptions ({validation.descriptionCount}/{DESCRIPTION_CAP})
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-teal-dark hover:text-teal-dark"
              disabled={rsa.descriptions.length >= DESCRIPTION_CAP}
              onClick={() => addDescription(cluster, idx)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add description
            </Button>
          </div>
          {rsa.descriptions.map((description, dIdx) => (
            <div key={dIdx} className="flex items-center gap-2">
              <CharCountInput
                value={description.text}
                max={DESCRIPTION_MAX}
                ariaLabel={`Description ${dIdx + 1}`}
                placeholder="Description text"
                invalid={validation.descriptionAllCaps[dIdx]}
                onChange={(text) => setDescriptionText(cluster, idx, dIdx, text)}
              />
              <PinSelect
                value={description.pin}
                positions={[1, 2]}
                ariaLabel={`Description ${dIdx + 1} pin`}
                onChange={(pin) =>
                  setDescriptionPin(cluster, idx, dIdx, pin as DescriptionPin)
                }
              />
              <div className="flex shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={dIdx === 0}
                  onClick={() => moveDescription(cluster, idx, dIdx, -1)}
                  aria-label="Move description up"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={dIdx === rsa.descriptions.length - 1}
                  onClick={() => moveDescription(cluster, idx, dIdx, 1)}
                  aria-label="Move description down"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={rsa.descriptions.length <= 1}
                  onClick={() => removeDescription(cluster, idx, dIdx)}
                  aria-label="Remove description"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {validation.descriptionCount < DESCRIPTION_MIN ? (
            <p className="text-[11px] text-destructive">
              Need at least {DESCRIPTION_MIN} descriptions (≤{DESCRIPTION_MAX} chars each).
            </p>
          ) : null}
          {validation.descriptionOverLimit ? (
            <p className="text-[11px] text-destructive">
              One or more descriptions exceed {DESCRIPTION_MAX} characters.
            </p>
          ) : null}
          {validation.descriptionAllCaps.some(Boolean) ? (
            <p className="text-[11px] text-destructive">
              Descriptions must not be ALL CAPS — Google rejects them. Use
              sentence case instead.
            </p>
          ) : null}
        </div>

        {/* Display paths */}
        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground">
            Display path (optional)
          </span>
          <div className="flex flex-wrap gap-2">
            <CharCountInput
              value={rsa.path1}
              max={PATH_MAX}
              ariaLabel="Path 1"
              placeholder="Path 1"
              onChange={(value) => setPath(cluster, idx, 'path1', value)}
            />
            <CharCountInput
              value={rsa.path2}
              max={PATH_MAX}
              ariaLabel="Path 2"
              placeholder="Path 2"
              onChange={(value) => setPath(cluster, idx, 'path2', value)}
            />
          </div>
          {validation.pathOverLimit ? (
            <p className="text-[11px] text-destructive">
              Each display path must be {PATH_MAX} characters or fewer.
            </p>
          ) : null}
        </div>
      </CardContent>
      </CollapsibleContent>
      </Collapsible>
    </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
        <div className="font-medium">Review ad copy before it goes live</div>
        <div className="mt-1 text-xs">
          Edit the AI-generated responsive search ads for each cluster. Each ad needs at
          least {HEADLINE_MIN} headlines (≤{HEADLINE_MAX} chars) and {DESCRIPTION_MIN}{' '}
          descriptions (≤{DESCRIPTION_MAX} chars). Pin a headline or description to lock
          it to a served position.
        </div>
      </div>

      {Object.entries(draftState).map(([cluster, rsas]) => {
        const clusterFallback = isUnresolvedFallback[cluster] ?? [];
        const clusterValid =
          (validations[cluster]?.every((v) => v.valid) ?? false) &&
          !clusterFallback.some(Boolean);
        return (
          <Collapsible key={cluster} defaultOpen className="rounded-md border border-border">
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{cluster}</span>
                <Badge variant="outline" className="text-[11px]">
                  {rsas.length} ad{rsas.length === 1 ? '' : 's'}
                </Badge>
              </div>
              {clusterValid ? (
                <Badge
                  variant="outline"
                  className="border-teal/40 text-[11px] font-normal text-teal-dark"
                >
                  Ready
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="border-destructive/40 text-[11px] font-normal text-destructive"
                >
                  Needs attention
                </Badge>
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 px-3 pb-3">
              {rsas.map((rsa, idx) =>
                renderRsaCard(
                  cluster,
                  idx,
                  rsa,
                  validations[cluster][idx],
                  clusterFallback[idx] ?? false,
                  strengths[cluster][idx],
                ),
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed text-teal-dark hover:text-teal-dark"
                disabled={rsas.length >= RSA_MAX || submitting || Boolean(regeneratingKey)}
                onClick={() => addAd(cluster)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add ad{rsas.length >= RSA_MAX ? ` (max ${RSA_MAX})` : ''}
              </Button>
            </CollapsibleContent>
          </Collapsible>
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        {submitting ? (
          // Morph: once submitted the footer gives way to the megaphone loader
          // until the 5s poll moves the run off AD_REVIEW (unmounting the editor).
          <CampaignBuildingLoader variant="inline" stage="Submitting ads — starting ad creation…" />
        ) : (
          <>
            <Button
              onClick={() => onSubmit(buildEditedAdCopy())}
              disabled={!isValid || submitting || Boolean(regeneratingKey)}
              className="bg-coral text-white shadow-md hover:bg-coral-hover hover:shadow-lg"
            >
              <Send className="mr-2 h-4 w-4" /> Submit ads &amp; continue
            </Button>
            {!isValid ? (
              <span className="self-center text-xs text-destructive">
                {hasUnresolvedFallback
                  ? 'Edit or regenerate the placeholder ads before submitting.'
                  : 'Fix the highlighted ads before submitting.'}
              </span>
            ) : null}
            <Button variant="outline" onClick={onReject}>
              Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default RsaEditor;
